import { cn } from "@/utils";
import { ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";

export type Country = {
  code: string;
  name: string;
  flag: string;
  ddi: string;
  /** Quantidade de dígitos do número nacional (sem DDI). */
  length: number;
  /** Máscara de exibição; cada `#` representa um dígito. */
  mask: string;
  /**
   * Comprimento mínimo aceito quando o país admite mais de um formato
   * (ex.: Brasil aceita fixo com 10 ou celular com 11). Quando ausente,
   * apenas `length` é considerado completo.
   */
  minLength?: number;
  /** Máscara usada quando o número tem `minLength` dígitos. */
  altMask?: string;
};

export const PHONE_COUNTRIES: Country[] = [
  // Brasil primeiro (mercado principal); demais em ordem alfabética.
  // A máscara dos países com DDD/código de área usa parênteses no prefixo.
  { code: "BR", name: "Brasil", flag: "🇧🇷", ddi: "55", length: 11, mask: "(##)#####-####", minLength: 10, altMask: "(##)####-####" }, // prettier-ignore
  { code: "PY", name: "Paraguai", flag: "🇵🇾", ddi: "595", length: 9, mask: "(###) ###-###" }, // prettier-ignore
  { code: "UY", name: "Uruguai", flag: "🇺🇾", ddi: "598", length: 8, mask: "#### ####" },
];

/**
 * Escolhe a máscara conforme a quantidade de dígitos. Países com formato
 * alternativo (ex.: Brasil fixo/celular) usam `altMask` enquanto o número
 * ainda não passou de `minLength`.
 */
const pickMask = (digits: string, country: Country): string => {
  if (
    country.altMask &&
    country.minLength &&
    digits.length <= country.minLength
  ) {
    return country.altMask;
  }
  return country.mask;
};

/** Aplica os dígitos na máscara do país (cada `#` consome um dígito). */
const applyMask = (digits: string, mask: string): string => {
  let result = "";
  let i = 0;
  for (const char of mask) {
    if (i >= digits.length) break;
    if (char === "#") {
      result += digits[i];
      i++;
    } else {
      result += char;
    }
  }
  return result;
};

/**
 * Normaliza uma string colada/digitada para os dígitos nacionais do país,
 * removendo máscara e o DDI quando presente.
 */
const sanitizePhone = (raw: string, country: Country): string => {
  let digits = raw.replace(/\D/g, "");
  if (digits.length > country.length && digits.startsWith(country.ddi)) {
    digits = digits.slice(country.ddi.length);
  }
  return digits.slice(0, country.length);
};

const CountrySelect = ({
  country,
  onChange,
}: {
  country: Country;
  onChange: (country: Country) => void;
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="flex h-[41px] cursor-pointer items-center gap-1.5 rounded-sm border border-gray-200 px-2.5 text-base text-black transition-colors hover:border-gray-300"
      >
        <span className="text-base leading-none">{country.flag}</span>
        <span className="font-medium">+{country.ddi}</span>
        <ChevronDown
          className={cn(
            "size-4 text-gray-500 transition-transform",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute top-full left-0 z-20 mt-1 max-h-72 w-64 overflow-y-auto rounded-lg border border-gray-200 bg-white py-1 shadow-[0_8px_24px_#00000018]"
        >
          {PHONE_COUNTRIES.map(c => (
            <li key={c.code}>
              <button
                type="button"
                role="option"
                aria-selected={c.code === country.code}
                onClick={() => {
                  onChange(c);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full cursor-pointer items-center gap-2 px-3 py-2 text-left text-sm text-black hover:bg-gray-100",
                  c.code === country.code && "bg-gray-50 font-medium"
                )}
              >
                <span className="text-base leading-none">{c.flag}</span>
                <span className="flex-1 truncate">{c.name}</span>
                <span className="whitespace-nowrap text-gray-500">+{c.ddi}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

type PhoneInputProps = {
  value: string;
  onChange: (digits: string) => void;
  country: Country;
  onCountryChange: (country: Country) => void;
  label?: string;
  errorMessage?: string;
  autoFocus?: boolean;
};

export const PhoneInput = ({
  value,
  onChange,
  country,
  onCountryChange,
  label,
  errorMessage,
  autoFocus,
}: PhoneInputProps) => {
  const digits = sanitizePhone(value, country);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(sanitizePhone(event.target.value, country));
  };

  return (
    <div className="flex flex-col gap-1.5">
      {label && <span className="text-sm text-black">{label}</span>}
      <div className="flex items-center gap-2">
        <CountrySelect country={country} onChange={onCountryChange} />
        <div
          className={cn(
            "flex h-[41px] w-full items-center rounded-sm border px-2",
            errorMessage ? "border-red" : "border-gray-200"
          )}
        >
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel-national"
            autoFocus={autoFocus}
            maxLength={country.mask.length}
            aria-label="Número de celular"
            value={applyMask(digits, pickMask(digits, country))}
            onChange={handleChange}
            placeholder={country.mask.replace(/#/g, "0")}
            className="w-full min-w-0 flex-1 text-black outline-none placeholder:text-gray-500"
          />
        </div>
      </div>
      {errorMessage && <span className="text-xs text-red">{errorMessage}</span>}
    </div>
  );
};
